/**
 * BottomComposer
 * Fixed text input at the bottom of the plan page
 * Used for custom entries and future step interactions
 */

import React, { useState, useEffect, useRef } from 'react';
import { Button } from '../../../components/ui/button';
import { Input } from '../../../components/ui/input';
import { Send, X } from 'lucide-react';

export default function BottomComposer({
  placeholder = 'Type here...',
  onSubmit,
  mode = null, // null | 'custom_goal' | 'custom_offer' | 'message'
  onCancel,
  disabled = false
}) {
  const [value, setValue] = useState('');
  const inputRef = useRef(null);

  useEffect(() => {
    if (mode && inputRef.current) {
      inputRef.current.focus();
    }
  }, [mode]);

  const handleSubmit = () => {
    if (value.trim() && onSubmit) {
      onSubmit(value.trim());
      setValue('');
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSubmit();
    }
    if (e.key === 'Escape' && onCancel) {
      onCancel();
    }
  };

  // Get placeholder based on mode
  const getPlaceholder = () => {
    switch (mode) {
      case 'custom_goal':
        return 'Enter a custom goal...';
      case 'custom_offer':
        return 'Enter a custom offer/CTA...';
      case 'message':
        return 'Type a message...';
      default:
        return placeholder;
    }
  };

  // If no mode is active, show inactive state
  if (!mode) {
    return (
      <div className="fixed bottom-0 left-0 right-0 bg-background/95 backdrop-blur border-t border-border p-4" data-testid="bottom-composer">
        <div className="max-w-3xl mx-auto">
          <div className="flex items-center gap-2 text-muted-foreground text-sm bg-muted/50 rounded-xl px-4 py-3">
            Complete the current step to continue...
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="fixed bottom-0 left-0 right-0 bg-background/95 backdrop-blur border-t border-border p-4" data-testid="bottom-composer">
      <div className="max-w-3xl mx-auto">
        <div className="flex items-center gap-2">
          <div className="flex-1 relative">
            <Input
              ref={inputRef}
              value={value}
              onChange={(e) => setValue(e.target.value)}
              onKeyDown={handleKeyDown}
              placeholder={getPlaceholder()}
              disabled={disabled}
              className="pr-20 h-12 rounded-xl"
              data-testid="composer-input"
            />
            {onCancel && (
              <Button
                variant="ghost"
                size="sm"
                onClick={onCancel}
                className="absolute right-12 top-1/2 -translate-y-1/2 h-8 w-8 p-0"
              >
                <X className="w-4 h-4" />
              </Button>
            )}
            <Button
              onClick={handleSubmit}
              disabled={!value.trim() || disabled}
              className="absolute right-1 top-1/2 -translate-y-1/2 h-10 w-10 p-0 rounded-lg"
              data-testid="composer-submit"
            >
              <Send className="w-4 h-4" />
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}
